import React, { useEffect, useState } from 'react';
import { TOP_BAR } from '../constants/content';
const COUNTDOWN_SECONDS = 15 * 60;
const CYCLE_RESET_MS = 60 * 60 * 1000;
const DEADLINE_KEY = 'pr2026_offer_deadline';
const START_KEY = 'pr2026_offer_started_at';
type OfferWindow = {
  deadline: number;
  startedAt: number;
};

/**
 * Persists both the beginning and end of a visitor's 15-minute offer window.
 * Leaving and coming back resumes the same countdown; a brand-new cycle only
 * starts once the stored beginning is at least an hour old.
 */
function resolveOfferWindow(): OfferWindow {
  const now = Date.now();
  const fresh = {
    startedAt: now,
    deadline: now + COUNTDOWN_SECONDS * 1000
  };
  if (typeof window === 'undefined') return fresh;
  try {
    const storedStart = Number.parseInt(window.localStorage.getItem(START_KEY) ?? '', 10);
    const storedDeadline = Number.parseInt(window.localStorage.getItem(DEADLINE_KEY) ?? '', 10);

    // Fall back to the stored deadline if only that one survived.
    const startedAt = Number.isNaN(storedStart) ? Number.isNaN(storedDeadline) ? NaN : storedDeadline - COUNTDOWN_SECONDS * 1000 : storedStart;
    if (!Number.isNaN(startedAt) && now - startedAt < CYCLE_RESET_MS) {
      const deadline = Number.isNaN(storedDeadline) ? startedAt + COUNTDOWN_SECONDS * 1000 : storedDeadline;
      window.localStorage.setItem(START_KEY, String(startedAt));
      window.localStorage.setItem(DEADLINE_KEY, String(deadline));
      return {
        startedAt,
        deadline
      };
    }
    window.localStorage.setItem(START_KEY, String(fresh.startedAt));
    window.localStorage.setItem(DEADLINE_KEY, String(fresh.deadline));
    return fresh;
  } catch {
    // Storage unavailable (private mode, etc.) — keep a non-persistent timer.
    return fresh;
  }
}
function formatCountdown(secondsLeft: number): string {
  const clamped = Math.max(0, secondsLeft);
  const minutes = String(Math.floor(clamped / 60)).padStart(2, '0');
  const seconds = String(clamped % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}
export function StickyAlertBar() {
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  useEffect(() => {
    const {
      deadline
    } = resolveOfferWindow();
    const tick = () => {
      const remaining = Math.ceil((deadline - Date.now()) / 1000);
      setSecondsLeft(Math.max(0, remaining));
    };
    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, []);
  const isExpired = secondsLeft === 0;
  return <div className="offer-bar-pulse sticky top-0 z-50 w-full text-cream" role="status" aria-live="polite" data-id="element-261" data-mp="jjxlaimguj0n">
      <div className="mx-auto flex min-h-[44px] max-w-6xl items-center justify-center px-3 py-2 text-center b640:px-4" data-id="element-262" data-mp="ekkph8dma1lx">
        <p className="text-[11px] font-600 leading-snug tracking-wide b600:text-xs b820:text-sm" data-id="element-263" data-mp="34bzfr25gg338">
          {TOP_BAR.prefix}{' '}
          <span className="whitespace-nowrap font-display text-xs font-700 tabular-nums text-mustard b600:text-sm b820:text-base" data-id="element-264" data-mp="1etkb15472s4g">
            {formatCountdown(secondsLeft)}
          </span>
          {isExpired && <span data-id="element-265" data-mp="1os04vfulwfhn"> {TOP_BAR.lastCall}</span>} {TOP_BAR.suffix}
        </p>
      </div>
    </div>;
}